import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { ContactAttributeApiService } from '../services/contact-attribute-api.service';
import { ContactAttributeRequest, ContactAttributeResponse } from '../models/contact-attribute.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ToastService } from '../../../shared/services/toast.service';
import { normalizeForComparison } from '../../../shared/utils/text-normalization.utils';

@Component({
  selector: 'app-contact-attribute-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent
  ],
  templateUrl: './contact-attribute-form.component.html'
})
export class ContactAttributeFormComponent implements OnInit {
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  attributeId: number | null = null;

  // Form model
  attributeName = '';
  displayName = '';
  private originalDisplayName = '';
  valueType: 'STRING' | 'NUMBER' | 'FK' = 'STRING';
  isActive = true;

  private existingAttributes: ContactAttributeResponse[] = [];

  constructor(
    private attributeApi: ContactAttributeApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService
  ) { }

  ngOnInit(): void {
    this.cargarAtributosExistentes();
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.attributeId = +id;
      this.cargarAtributo(this.attributeId);
    }
  }

  get pageTitle(): string {
    return this.isEditing ? 'Editar Atributo' : 'Nuevo Atributo';
  }

  private cargarAtributosExistentes(): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;
    this.attributeApi.getAttributes(businessId).subscribe({
      next: (data) => { this.existingAttributes = data; },
      error: () => { /* validación se delega al backend si falla */ }
    });
  }

  private cargarAtributo(id: number): void {
    this.loading = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.attributeApi.getAttributeById(id, businessId).subscribe({
      next: (atributo) => {
        this.attributeName = atributo.attributeName;
        this.displayName = atributo.displayName;
        this.originalDisplayName = atributo.displayName;
        this.valueType = atributo.valueType;
        this.isActive = atributo.isActive;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el atributo';
        this.loading = false;
      }
    });
  }

  private esDuplicado(): { duplicado: boolean; campo?: 'attributeName' | 'displayName' } {
    const targetAttributeName = normalizeForComparison(this.attributeName);
    const targetDisplayName = normalizeForComparison(this.displayName);
    for (const a of this.existingAttributes) {
      if (a.id === this.attributeId) continue;
      if (normalizeForComparison(a.attributeName) === targetAttributeName) return { duplicado: true, campo: 'attributeName' };
      if (normalizeForComparison(a.displayName) === targetDisplayName) return { duplicado: true, campo: 'displayName' };
    }
    return { duplicado: false };
  }

  guardar(): void {
    const faltantes: string[] = [];
    if (!this.attributeName.trim()) faltantes.push('Nombre del atributo');
    if (!this.displayName.trim()) faltantes.push('Etiqueta');

    if (faltantes.length > 0) {
      this.error = `Complete los campos requeridos: ${faltantes.join(', ')}.`;
      this.toast.warning({ title: 'Campos incompletos', message: this.error });
      return;
    }

    const dup = this.esDuplicado();
    if (dup.duplicado) {
      const campo = dup.campo === 'attributeName' ? 'nombre del atributo' : 'etiqueta visible';
      const valor = dup.campo === 'attributeName' ? this.attributeName.trim() : this.displayName.trim();
      const msg = `Ya existe un atributo con el ${campo} "${valor}"`;
      this.error = msg;
      this.toast.warning({ title: `${campo[0].toUpperCase() + campo.slice(1)} duplicado`, message: msg });
      return;
    }

    this.saving = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    const request: ContactAttributeRequest = {
      businessId,
      attributeName: this.attributeName.trim(),
      displayName: this.displayName.trim(),
      valueType: this.valueType,
      isActive: this.isActive
    };

    if (this.isEditing && this.attributeId) {
      this.attributeApi.updateAttribute(this.attributeId, request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.updated('Atributo de contacto',
            this.originalDisplayName !== this.displayName.trim()
              ? `Se actualizó "${this.originalDisplayName}" → "${this.displayName.trim()}".`
              : `Se actualizó "${this.displayName.trim()}" correctamente.`);
          this.router.navigate(['/config-contactos/atributos']);
        },
        error: (err) => {
          this.saving = false;
          if (err.status === 409) {
            this.error = err.error?.message || 'Ya existe un atributo con esos datos';
            this.toast.warning({ title: 'Atributo duplicado', message: this.error });
          } else {
            const msg = err.error?.message || 'Error al actualizar el atributo';
            this.error = msg;
            this.toast.error({ title: 'Error', message: msg });
          }
        }
      });
    } else {
      this.attributeApi.createAttribute(request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.created('Atributo de contacto', `Se creó "${this.displayName.trim()}" correctamente.`);
          this.router.navigate(['/config-contactos/atributos']);
        },
        error: (err) => {
          this.saving = false;
          if (err.status === 409) {
            this.error = err.error?.message || 'Ya existe un atributo con esos datos';
            this.toast.warning({ title: 'Atributo duplicado', message: this.error });
          } else {
            const msg = err.error?.message || 'Error al crear el atributo';
            this.error = msg;
            this.toast.error({ title: 'Error', message: msg });
          }
        }
      });
    }
  }

  cancelar(): void {
    this.router.navigate(['/config-contactos/atributos']);
  }
}
